import { __assign, __awaiter, __extends, __generator, __rest, __values } from "tslib";
import { openDB } from 'idb';
import { STORAGE_FAILURE } from '../messages';
import { BaseEventsStore } from './base-events-store';
import { logIdbError } from '../utils/is-abort-error';
// crypto.randomUUID() requires a secure context (https). Fall back to a
// Math.random-based UUID for http origins or older browsers — tab IDs don't
// need to be cryptographically secure, just unique within a session.
export function generateUUID() {
    try {
        return crypto.randomUUID();
    }
    catch (_a) {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    }
}
export var currentSequenceKey = 'sessionCurrentSequence';
export var sequencesToSendKey = 'sequencesToSend';
export var remoteConfigKey = 'remoteConfig';
// Timeout for openDB at init.  IDB openDB can hang forever when another tab
// holds an open connection during a version upgrade, or when the DB is in a
// "closing" state (documented Chrome behaviour).  When that happens we want
// SessionReplayEventsIDBStore.new() to bail out so the caller can fall back
// to the in-memory store.
export var OPEN_DB_TIMEOUT_MS = 2000;
// Timeout for per-operation tx.done settlement.  Mid-recording a readwrite
// transaction can stall (storage pressure in some browsers stalls instead of
// throwing); without a timeout, recordFailure() is never called and the
// memory fallback never triggers.  The transaction may still settle later;
// the timedOut flag prevents double-counting alongside errorLogged.
export var TX_DONE_TIMEOUT_MS = 5000;
/**
 * Race a promise against a timeout.  Resolves/rejects with the original
 * promise's value when it settles first; rejects with a timeout error if
 * `ms` elapses first.  Either way the timer is cleared so we don't leak
 * pending setTimeouts.
 */
export function withTimeout(promise, ms, message) {
    if (message === void 0) { message = 'IDB operation timed out'; }
    return new Promise(function (resolve, reject) {
        var timer = setTimeout(function () { return reject(new Error("".concat(message, " after ").concat(ms, "ms"))); }, ms);
        promise.then(function (v) {
            clearTimeout(timer);
            resolve(v);
        }, function (e) {
            clearTimeout(timer);
            reject(e);
        });
    });
}
/**
 * Arms a watchdog that fires `onTimeout` only if `tx.done` hasn't settled
 * within `ms`.  A "soft cancel" returned to the caller suppresses the timeout
 * if the synchronous operation body completes without exception — important
 * because in test environments fake timers can fire ahead of tx.done's commit
 * microtask.  Production behaviour is preserved: a genuinely stalled
 * transaction (no commit, no error, individual op promises never resolved)
 * cannot reach the soft-cancel call and the timeout fires as designed.
 *
 * Soft-cancel is only invoked once the awaits inside the operation's outer
 * try block have all returned — i.e. the IDB driver acknowledged each
 * individual put/get.  If the driver accepts a put but the underlying
 * transaction silently fails to commit (the production stall scenario),
 * tx.done still hasn't settled, and recordFailure must NOT have been
 * suppressed.  The fix for that scenario in production is the tx.done.catch
 * handler attached separately by callers, which catches the eventual abort.
 *
 * The soft-cancel pattern only suppresses the timeout for the "all puts
 * resolved successfully" case — a case where tx.done is overwhelmingly
 * likely to settle imminently in production.  If it doesn't, callers can
 * still detect the failure on the NEXT operation (which will fail to open
 * a transaction or hit the same pressure), because all three methods use
 * readwrite transactions on the same two stores, which IDB serializes:
 * if T1's tx.done never settles, T2 is blocked waiting for T1 to commit or
 * abort — T2's put/get requests never resolve, T2 never reaches its
 * soft-cancel, and T2's watchdog fires, calling recordFailure().
 */
function armTxDoneTimeout(txDone, ms, onTimeout) {
    var timer = setTimeout(onTimeout, ms);
    // Belt-and-braces: clear the timer when tx.done settles, even though the
    // primary cancel path is the caller's success-path cancel().  This covers
    // the case where tx.done settles with no caller cancellation (shouldn't
    // happen in current code paths, but cheap insurance).
    txDone.then(function () { return clearTimeout(timer); }, function () { return clearTimeout(timer); });
    return function () { return clearTimeout(timer); };
}
export var defineObjectStores = function (db) {
    var sequencesStore;
    var currentSequenceStore;
    if (!db.objectStoreNames.contains(currentSequenceKey)) {
        currentSequenceStore = db.createObjectStore(currentSequenceKey, {
            keyPath: 'sessionId',
        });
    }
    if (!db.objectStoreNames.contains(sequencesToSendKey)) {
        sequencesStore = db.createObjectStore(sequencesToSendKey, {
            keyPath: 'sequenceId',
            autoIncrement: true,
        });
        sequencesStore.createIndex('sessionId', 'sessionId');
    }
    return {
        sequencesStore: sequencesStore,
        currentSequenceStore: currentSequenceStore,
    };
};
export var createStore = function (dbName) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0: return [4 /*yield*/, withTimeout(openDB(dbName, 1, {
                    upgrade: defineObjectStores,
                }), OPEN_DB_TIMEOUT_MS, 'IDB openDB timed out')];
            case 1: 
            // Wrap openDB with a timeout so a hung connection (foreign tab holding an
            // open handle during version upgrade, or "closing" DB) doesn't block the
            // SDK from initialising.  On timeout this rejects, which propagates up to
            // SessionReplayEventsIDBStore.new()'s catch block, returning undefined and
            // triggering the memory fallback.
            return [2 /*return*/, _a.sent()];
        }
    });
}); };
var SessionReplayEventsIDBStore = /** @class */ (function (_super) {
    __extends(SessionReplayEventsIDBStore, _super);
    function SessionReplayEventsIDBStore(args) {
        var _this = this;
        var _a;
        _this = _super.call(this, args) || this;
        _this.consecutiveFailures = 0;
        _this.hasTriggeredFallback = false;
        _this.emptyFilteredCount = 0;
        _this.getSequencesToSend = function () { return __awaiter(_this, void 0, void 0, function () {
            var errorLogged, timedOut, sequences, tx, cancelTimeout, cursor, _a, sessionId, events, e_1;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        errorLogged = false;
                        timedOut = false;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 9, , 10]);
                        sequences = [];
                        tx = this.db.transaction('sequencesToSend', 'readwrite');
                        // Attach a catch handler immediately so tx.done rejections (e.g. AbortError after
                        // cursor traversal completes) are always handled without blocking the return path.
                        // The errorLogged / timedOut flags prevent double-logging and double-recording
                        // when the outer catch (or the timeout race) already fired for the same abort.
                        tx.done.catch(function (e) {
                            if (!errorLogged && !timedOut) {
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e), e);
                                _this.recordFailure();
                            }
                        });
                        cancelTimeout = armTxDoneTimeout(tx.done, TX_DONE_TIMEOUT_MS, function () {
                            if (!errorLogged && !timedOut) {
                                timedOut = true;
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": transaction timed out"));
                                _this.recordFailure();
                            }
                        });
                        return [4 /*yield*/, tx.store.openCursor()];
                    case 2:
                        cursor = _b.sent();
                        _b.label = 3;
                    case 3:
                        if (!cursor) return [3 /*break*/, 8];
                        _a = cursor.value, sessionId = _a.sessionId, events = _a.events;
                        if (!(events.length === 0)) return [3 /*break*/, 5];
                        this.maybeLogEmptyFiltered('getSequencesToSend');
                        return [4 /*yield*/, cursor.delete()];
                    case 4:
                        _b.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        // Return all completed sequences regardless of tabId.  Filtering by tab
                        // would cause event loss on page reload: a new store instance gets a
                        // fresh in-memory UUID and would never see sequences written by the
                        // previous instance.  Completed sequences are safe to flush by any
                        // tab/instance; the server deduplicates, and cleanUpSessionEventsStore
                        // on an already-deleted key is a no-op.
                        sequences.push({
                            events: events,
                            sequenceId: cursor.key,
                            sessionId: sessionId,
                        });
                        _b.label = 6;
                    case 6: return [4 /*yield*/, cursor.continue()];
                    case 7:
                        cursor = _b.sent();
                        return [3 /*break*/, 3];
                    case 8:
                        this.recordSuccess();
                        cancelTimeout();
                        return [2 /*return*/, sequences];
                    case 9:
                        e_1 = _b.sent();
                        if (!timedOut) {
                            errorLogged = true;
                            logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_1), e_1);
                            this.recordFailure();
                        }
                        return [3 /*break*/, 10];
                    case 10: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.storeCurrentSequence = function (sessionId) { return __awaiter(_this, void 0, void 0, function () {
            var errorLogged, timedOut, tx, cancelTimeout, currentSequenceData, sequenceId, _tabId, rest, e_2;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        errorLogged = false;
                        timedOut = false;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 5, , 6]);
                        tx = this.db.transaction([currentSequenceKey, sequencesToSendKey], 'readwrite');
                        tx.done.catch(function (e) {
                            if (!errorLogged && !timedOut) {
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e), e);
                                _this.recordFailure();
                            }
                        });
                        cancelTimeout = armTxDoneTimeout(tx.done, TX_DONE_TIMEOUT_MS, function () {
                            if (!errorLogged && !timedOut) {
                                timedOut = true;
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": transaction timed out"));
                                _this.recordFailure();
                            }
                        });
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).get(sessionId)];
                    case 2:
                        currentSequenceData = _a.sent();
                        // Skip promotion if the slot is empty or owned by another tab — let the owning
                        // tab promote its own events on its next addEventToCurrentSequence/storeCurrentSequence
                        // call (via Bug 1's foreign-tab promotion path).
                        // Don't call recordSuccess() here: no write was performed, so this is not
                        // evidence the storage layer is healthy — leave the failure counter unchanged.
                        if (!currentSequenceData || (currentSequenceData.tabId && currentSequenceData.tabId !== this.tabId)) {
                            cancelTimeout();
                            return [2 /*return*/, undefined];
                        }
                        // Skip empty sequences — no point writing a zero-event row to sequencesToSend
                        // (would later POST as an empty body and 400 on the server).
                        if (currentSequenceData.events.length === 0) {
                            this.maybeLogEmptyFiltered('storeCurrentSequence');
                            cancelTimeout();
                            return [2 /*return*/, undefined];
                        }
                        return [4 /*yield*/, tx.objectStore(sequencesToSendKey).put({
                                sessionId: sessionId,
                                events: currentSequenceData.events,
                                tabId: this.tabId,
                            })];
                    case 3:
                        sequenceId = _a.sent();
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({
                                sessionId: sessionId,
                                events: [],
                                tabId: this.tabId,
                            })];
                    case 4:
                        _a.sent();
                        this.recordSuccess();
                        cancelTimeout();
                        _tabId = currentSequenceData.tabId, rest = __rest(currentSequenceData, ["tabId"]);
                        return [2 /*return*/, __assign(__assign({}, rest), { sessionId: sessionId, sequenceId: sequenceId })];
                    case 5:
                        e_2 = _a.sent();
                        if (!timedOut) {
                            errorLogged = true;
                            logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_2), e_2);
                            this.recordFailure();
                        }
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.addEventToCurrentSequence = function (sessionId, event) { return __awaiter(_this, void 0, void 0, function () {
            var errorLogged, timedOut, tx, cancelTimeout, sequenceEvents, ownedSequence, eventsToSend, sequenceId, e_3;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        errorLogged = false;
                        timedOut = false;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 15, , 16]);
                        tx = this.db.transaction([currentSequenceKey, sequencesToSendKey], 'readwrite');
                        // Attach a catch handler immediately so tx.done rejections (e.g. AbortError after
                        // put succeeds but before auto-commit) are always handled without blocking.
                        // The errorLogged / timedOut flags prevent double-logging when the outer catch
                        // (or the timeout) already fired for the same transaction.
                        tx.done.catch(function (e) {
                            if (!errorLogged && !timedOut) {
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e), e);
                                _this.recordFailure();
                            }
                        });
                        cancelTimeout = armTxDoneTimeout(tx.done, TX_DONE_TIMEOUT_MS, function () {
                            if (!errorLogged && !timedOut) {
                                timedOut = true;
                                logIdbError(_this.loggerProvider, "".concat(STORAGE_FAILURE, ": transaction timed out"));
                                _this.recordFailure();
                            }
                        });
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).get(sessionId)];
                    case 2:
                        sequenceEvents = _a.sent();
                        if (!((sequenceEvents === null || sequenceEvents === void 0 ? void 0 : sequenceEvents.tabId) && sequenceEvents.tabId !== this.tabId)) return [3 /*break*/, 6];
                        if (!(sequenceEvents.events.length > 0)) return [3 /*break*/, 4];
                        return [4 /*yield*/, tx.objectStore(sequencesToSendKey).put({
                                sessionId: sessionId,
                                events: sequenceEvents.events,
                                tabId: sequenceEvents.tabId,
                            })];
                    case 3:
                        _a.sent();
                        _a.label = 4;
                    case 4: return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({ sessionId: sessionId, events: [event], tabId: this.tabId })];
                    case 5:
                        _a.sent();
                        this.recordSuccess();
                        cancelTimeout();
                        return [2 /*return*/, undefined];
                    case 6:
                        ownedSequence = sequenceEvents;
                        if (!!ownedSequence) return [3 /*break*/, 8];
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({ sessionId: sessionId, events: [event], tabId: this.tabId })];
                    case 7:
                        _a.sent();
                        this.recordSuccess();
                        cancelTimeout();
                        return [2 /*return*/, undefined];
                    case 8:
                        if (!!this.shouldSplitEventsList(ownedSequence.events, event)) return [3 /*break*/, 10];
                        return [4 /*yield*/, tx
                                .objectStore(currentSequenceKey)
                                .put({ sessionId: sessionId, events: ownedSequence.events.concat(event), tabId: this.tabId })];
                    case 9:
                        _a.sent();
                        this.recordSuccess();
                        cancelTimeout();
                        return [2 /*return*/, undefined];
                    case 10:
                        eventsToSend = ownedSequence.events;
                        if (!(eventsToSend.length === 0)) return [3 /*break*/, 12];
                        this.maybeLogEmptyFiltered('addEventToCurrentSequence');
                        return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({ sessionId: sessionId, events: [event], tabId: this.tabId })];
                    case 11:
                        _a.sent();
                        this.recordSuccess();
                        cancelTimeout();
                        return [2 /*return*/, undefined];
                    case 12: return [4 /*yield*/, tx.objectStore(currentSequenceKey).put({ sessionId: sessionId, events: [event], tabId: this.tabId })];
                    case 13:
                        _a.sent();
                        return [4 /*yield*/, tx.objectStore(sequencesToSendKey).put({
                                sessionId: sessionId,
                                events: eventsToSend,
                                tabId: this.tabId,
                            })];
                    case 14:
                        sequenceId = _a.sent();
                        this.recordSuccess();
                        cancelTimeout();
                        return [2 /*return*/, {
                                events: eventsToSend,
                                sessionId: sessionId,
                                sequenceId: sequenceId,
                            }];
                    case 15:
                        e_3 = _a.sent();
                        if (!timedOut) {
                            errorLogged = true;
                            logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_3), e_3);
                            this.recordFailure();
                        }
                        return [3 /*break*/, 16];
                    case 16: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.storeSendingEvents = function (sessionId, events) { return __awaiter(_this, void 0, void 0, function () {
            var sequenceId, e_4;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, this.db.put(sequencesToSendKey, {
                                sessionId: sessionId,
                                events: events,
                                tabId: this.tabId,
                            })];
                    case 1:
                        sequenceId = _a.sent();
                        this.recordSuccess();
                        return [2 /*return*/, sequenceId];
                    case 2:
                        e_4 = _a.sent();
                        logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_4), e_4);
                        this.recordFailure();
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/, undefined];
                }
            });
        }); };
        _this.cleanUpSessionEventsStore = function (_sessionId, sequenceId) { return __awaiter(_this, void 0, void 0, function () {
            var e_5;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!sequenceId) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.db.delete(sequencesToSendKey, sequenceId)];
                    case 2:
                        _a.sent();
                        this.recordSuccess();
                        return [3 /*break*/, 4];
                    case 3:
                        e_5 = _a.sent();
                        logIdbError(this.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_5), e_5);
                        this.recordFailure();
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        _this.db = args.db;
        _this.tabId = args.tabId;
        _this.onPersistentFailure = args.onPersistentFailure;
        // Default threshold of 1: fall back to memory immediately on the first IDB failure.
        // Session replay correctness is far more important than persistence, and IDB errors
        // are typically the symptom of a deeper problem (storage pressure, locked DB, broken
        // browser implementation) that won't recover within a single session.  Memory store
        // is always safe — fall back early.
        _this.consecutiveFailureThreshold = (_a = args.consecutiveFailureThreshold) !== null && _a !== void 0 ? _a : 1;
        return _this;
    }
    // Sampled (1 in 100) debug log so we can observe whether the store-layer guards
    // are catching empty-batch cases that would otherwise hit the empty-body 400 path
    // on the server. Logged at debug, not warn — this is operational telemetry for
    // post-deploy verification, not a customer-actionable warning. Per-store-instance
    // counter (rather than Math.random) keeps the first hit deterministic for tests.
    SessionReplayEventsIDBStore.prototype.maybeLogEmptyFiltered = function (source) {
        if (this.emptyFilteredCount++ % 100 === 0) {
            this.loggerProvider.debug("Filtered empty session replay sequence at ".concat(source, " (idb store)"));
        }
    };
    SessionReplayEventsIDBStore.prototype.recordFailure = function () {
        var _a;
        this.consecutiveFailures++;
        if (!this.hasTriggeredFallback && this.consecutiveFailures >= this.consecutiveFailureThreshold) {
            this.hasTriggeredFallback = true;
            (_a = this.onPersistentFailure) === null || _a === void 0 ? void 0 : _a.call(this);
        }
    };
    SessionReplayEventsIDBStore.prototype.recordSuccess = function () {
        this.consecutiveFailures = 0;
    };
    SessionReplayEventsIDBStore.new = function (type, args) {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var dbSuffix, dbName, db, tabId, e_6;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        dbSuffix = type === 'replay' ? '' : "_".concat(type);
                        dbName = "".concat(args.apiKey.substring(0, 10), "_amp_session_replay_events").concat(dbSuffix);
                        return [4 /*yield*/, createStore(dbName)];
                    case 1:
                        db = _b.sent();
                        tabId = (_a = args.tabId) !== null && _a !== void 0 ? _a : generateUUID();
                        return [2 /*return*/, new SessionReplayEventsIDBStore(__assign(__assign({}, args), { db: db, tabId: tabId }))];
                    case 2:
                        e_6 = _b.sent();
                        logIdbError(args.loggerProvider, "".concat(STORAGE_FAILURE, ": ").concat(e_6), e_6);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    SessionReplayEventsIDBStore.prototype.getCurrentSequenceEvents = function (sessionId) {
        return __awaiter(this, void 0, void 0, function () {
            var record, _tabId, rest, allEvents, _a, _b, record, _tabId, rest, e_7_1;
            var e_7, _c;
            return __generator(this, function (_d) {
                switch (_d.label) {
                    case 0:
                        if (!sessionId) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.db.get('sessionCurrentSequence', sessionId)];
                    case 1:
                        record = _d.sent();
                        if (!record) {
                            return [2 /*return*/, undefined];
                        }
                        // Only return our own tab's record (or legacy untagged records).
                        if (record.tabId && record.tabId !== this.tabId) {
                            return [2 /*return*/, undefined];
                        }
                        _tabId = record.tabId, rest = __rest(record, ["tabId"]);
                        return [2 /*return*/, [rest]];
                    case 2:
                        allEvents = [];
                        _d.label = 3;
                    case 3:
                        _d.trys.push([3, 8, 9, 10]);
                        return [4 /*yield*/, this.db.getAll('sessionCurrentSequence')];
                    case 4:
                        _a = __values.apply(void 0, [_d.sent()]), _b = _a.next();
                        _d.label = 5;
                    case 5:
                        if (!!_b.done) return [3 /*break*/, 7];
                        record = _b.value;
                        if (record.tabId && record.tabId !== this.tabId) {
                            return [3 /*break*/, 6];
                        }
                        _tabId = record.tabId, rest = __rest(record, ["tabId"]);
                        allEvents.push(rest);
                        _d.label = 6;
                    case 6:
                        _b = _a.next();
                        return [3 /*break*/, 5];
                    case 7: return [3 /*break*/, 10];
                    case 8:
                        e_7_1 = _d.sent();
                        e_7 = { error: e_7_1 };
                        return [3 /*break*/, 10];
                    case 9:
                        try {
                            if (_b && !_b.done && (_c = _a.return)) _c.call(_a);
                        }
                        finally { if (e_7) throw e_7.error; }
                        return [7 /*endfinally*/];
                    case 10: return [2 /*return*/, allEvents];
                }
            });
        });
    };
    return SessionReplayEventsIDBStore;
}(BaseEventsStore));
export { SessionReplayEventsIDBStore };
//# sourceMappingURL=events-idb-store.js.map